import React from "react";
import { DateTime } from "luxon";
import {
  useRetrieveGantt,
  useRetrieveCurrentPlanzone,
  useRetrieveGanttCalendarRange,
  useRetrievePreferences,
} from "@planzone/view-models";
import { GanttSort } from "@planzone/dto";
import { useMessage } from "@components/useMessage";
import { Gantt } from "@scenes/optimizedGantt";
import { Props } from "./types";

function GanttPage(_: Props) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const { preferences } = useRetrievePreferences();
  const savedSort: GanttSort = (preferences[
    `${planzone.uuid}:ganttSort` as keyof typeof preferences
  ] || "ascendingRank") as GanttSort;
  const [sort, setSort] = React.useState<GanttSort>(savedSort);
  const {
    gantt,
    isRequestPending,
    currentPage,
    canRetrieveNextElements,
    retrieveGantt,
  } = useRetrieveGantt(50, sort);
  const {
    isRequestPending: isCalendarRequestPending,
    isRequestSuccess: isCalendarRequestSuccess,
    isRequestFailure: isCalendarRequestFailure,
    range,
    retrieveGanttCalendarRange,
  } = useRetrieveGanttCalendarRange();
  React.useEffect(() => {
    !isCalendarRequestPending &&
      !isCalendarRequestSuccess &&
      !isCalendarRequestFailure &&
      retrieveGanttCalendarRange({});
  }, []);

  useMessage(
    ({ type }) =>
      ["Task", "List", "Milestone", "Event"].find(
        (elementType) =>
          type === `create${elementType}Succeeded` ||
          type === `edit${elementType}Succeeded`,
      ) && retrieveGanttCalendarRange({}),
  );

  return (
    <Gantt
      projects={gantt.projects}
      range={
        !!range[0] && !!range[1]
          ? { minDate: range[0], maxDate: range[1] }
          : (() => {
              const now = DateTime.now().toUTC().toISO() ?? "";
              return { minDate: now, maxDate: now };
            })()
      }
      filter={{
        scope: "planzone",
        elementTypes: ["list", "task", "milestone", "event", "completedWork"],
      }}
      sort="ascendingRank"
      namespace={planzone.uuid}
      isFetchingProjects={isRequestPending}
      canRetrieveProjects={!isRequestPending && canRetrieveNextElements}
      retrieveProjects={() =>
        retrieveGantt({ size: 50, sort, page: currentPage })
      }
      selectSort={(nextSort) => sort !== nextSort && setSort(nextSort)}
    />
  );
}

export { GanttPage };
